// frontend/modules/customer-notification/components/dashboard/NotificationStatusChart.tsx
import React, { useMemo } from 'react';
import { Card, Empty, Spin } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { NotificationStatistics } from '@/shared/api/model';

interface NotificationStatusChartProps {
    statistics: NotificationStatistics | null;
    isLoading: boolean;
    chartType: 'platform' | 'category';
}

const NotificationStatusChart: React.FC<NotificationStatusChartProps> = ({ 
    statistics, 
    isLoading,
    chartType
}) => {
    const chartData = useMemo(() => {
        if (!statistics) return [];
        
        if (chartType === 'platform') {
            return Object.entries(statistics.notificationsByPlatform || {})
                .map(([platform, count]) => ({
                    name: platform,
                    value: count
                }));
        } else {
            return Object.entries(statistics.notificationsByCategory || {})
                .map(([category, count]) => ({
                    name: formatCategoryName(category),
                    value: count
                }));
        }
    }, [statistics, chartType]);
    
    const getColors = (type: string) => {
        if (type === 'platform') {
            return ['#1890ff', '#13c2c2', '#722ed1', '#eb2f96', '#faad14'];
        } else {
            return ['#52c41a', '#faad14', '#f5222d', '#1890ff', '#722ed1', '#fa8c16', '#a0d911', '#13c2c2', '#eb2f96'];
        }
    };
    
    return (
        <Card title={chartType === 'platform' ? "Notifications by Platform" : "Notifications by Category"} className="h-96">
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            ) : chartData.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {chartData.map((entry, index) => (
                                <Cell 
                                    key={`cell-${index}`} 
                                    fill={getColors(chartType)[index % getColors(chartType).length]} 
                                />
                            ))}
                        </Pie>
                        <Tooltip 
                            formatter={(value: number) => [value, 'Count']} 
                        />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </Card>
    );
};

// Helper function to format category names
const formatCategoryName = (category: string): string => {
    return category
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export default NotificationStatusChart;
